// src/pages/dashboard/components/KPICards/index.js (Robusto)
import React, { useState, useEffect } from 'react'; // Añadir useEffect si no está
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Switch,
  CircularProgress,
  TextField
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import SyncIcon from '@mui/icons-material/Sync';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import exchangeRateApi from '../../../../services/exchangeRateApi';

// Mini selector de tasa de cambio compacto (MODIFICADO para usar useEffect)
const CompactExchangeRateSelector = ({ onRateChange, initialRate }) => {
  const [isAutoMode, setIsAutoMode] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [sinTasa, setSinTasa] = useState(false);
  const [rate, setRate] = useState(initialRate || 0); // Default a 0 si initialRate es undefined
  const [editRate, setEditRate] = useState((initialRate || 0).toString());
  const [loading, setLoading] = useState(false);

  // Sincronizar estado interno si initialRate cambia desde fuera
  useEffect(() => {
    if (initialRate !== undefined && Math.abs(initialRate - rate) > 0.01) {
      setRate(initialRate);
      setEditRate(initialRate.toString());
      // Podrías querer verificar el modo aquí también si es necesario
      // fetchMode(); // Función hipotética para obtener el modo actual
    }
  }, [initialRate]); // Dependencia de initialRate

  // Función para actualizar tasa
  const fetchRate = async () => {
    setLoading(true);
    try {
      const { rate: newRate, mode } = await exchangeRateApi.getCurrentRate();

      // Sin tasa no se toca nada: se conserva la última conocida y se avisa en
      // pantalla. Antes `Math.abs(null - rate)` daba un número grande y entraba
      // acá con newRate en null, que reventaba en .toString().
      if (newRate === null || newRate === undefined) {
        setSinTasa(true);
      } else {
        setSinTasa(false);
        if (Math.abs(newRate - rate) > 0.01) {
          setRate(newRate);
          setEditRate(newRate.toString());
          if (onRateChange) {
            onRateChange(newRate); // Notificar al padre del cambio
          }
        }
      }
      setIsAutoMode(mode === 'auto'); // Actualizar estado del modo
    } catch (error) {
      console.error('Error al actualizar tasa:', error);
    } finally {
      setLoading(false);
    }
  };

  // Cambiar modo auto/manual
  const handleModeChange = async (event) => {
    const autoMode = event.target.checked;

    try {
      setLoading(true);
      if (autoMode) {
        const result = await exchangeRateApi.switchToAutoMode();
        setSinTasa(result.rate === null);
        if (result.rate !== null && Math.abs(result.rate - rate) > 0.01) {
          setRate(result.rate);
          setEditRate(result.rate.toString());
          if (onRateChange) {
            onRateChange(result.rate); // Notificar al padre
          }
        }
      } else {
        await exchangeRateApi.setManualRate(rate);
      }
      setIsAutoMode(autoMode);
      setIsEditing(false);
    } catch (error) {
      console.error('Error al cambiar modo:', error);
    } finally {
      setLoading(false);
    }
  };

  // Actualizar tasa manual
  const handleRateChangeInput = (e) => { // Renombrar para evitar conflicto
    setEditRate(e.target.value);
  };

  // Guardar tasa manual
  const saveManualRate = async () => {
    try {
      const numericRate = parseFloat(editRate) || 0;
      if (numericRate > 0 && Math.abs(numericRate - rate) > 0.01) {
        await exchangeRateApi.setManualRate(numericRate);
        setRate(numericRate);
        if (onRateChange) {
          onRateChange(numericRate); // Notificar al padre
        }
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Error al guardar tasa manual:', error);
    }
  };

  // Modo de edición
  if (isEditing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <TextField
          type="text"
          value={editRate}
          onChange={handleRateChangeInput} // Usar nombre renombrado
          variant="outlined"
          size="small"
          sx={{
            width: '90px',
            '& .MuiOutlinedInput-root': {
              height: '24px',
              fontSize: '0.75rem',
              '& fieldset': { borderColor: '#444' },
              '&:hover fieldset': { borderColor: '#666' },
              '&.Mui-focused fieldset': { borderColor: '#2196F3' }
            },
            '& .MuiOutlinedInput-input': {
              color: 'white',
              p: '4px 6px'
            }
          }}
        />
        <IconButton size="small" onClick={saveManualRate} sx={{ p: 0.2, ml: 0.5 }}>
          <CheckIcon sx={{ fontSize: '0.8rem', color: '#4CAF50' }} />
        </IconButton>
        <IconButton size="small" onClick={() => setIsEditing(false)} sx={{ p: 0.2 }}>
          <CloseIcon sx={{ fontSize: '0.8rem', color: '#F44336' }} />
        </IconButton>
      </Box>
    );
  }

  // Visualización normal
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="caption" color="#8C8C8C" sx={{ fontSize: '0.7rem', mr: 0.5 }}>
          {/* Asegurarse que rate es un número antes de formatear */}
            {sinTasa
            ? `${typeof rate === 'number' ? rate.toFixed(2) : '—'} Bs./USD (sin conexión)`
            : `${typeof rate === 'number' ? rate.toFixed(2) : '—'} Bs./USD`}
        </Typography>
        {loading && <CircularProgress size={10} sx={{ color: '#8C8C8C', mr: 0.5 }} />}
        <Tooltip title="Volver a consultar la tasa">
          <IconButton onClick={fetchRate} size="small" sx={{ p: 0.2 }}>
            <RefreshIcon sx={{ fontSize: '0.8rem', color: '#8C8C8C' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/*
        Acá vivía un interruptor Auto/Manual con un lápiz para escribir la tasa a
        mano. Cambiaba sólo el equivalente aproximado que muestra esta tarjeta
        —no la tasa de las facturas, que la resuelve el servidor contra el BCV y
        queda guardada en cada documento— y se guardaba por navegador.

        Se fue por dos motivos. El dueño del producto preguntó dos veces para qué
        servía, incluso después de etiquetarlo: si él no le encuentra uso, el
        cliente tampoco. Y dejarlo en Manual con una tasa vieja hacía que el
        panel informara ingresos equivocados, avisando apenas con la palabra
        «Manual» en letra chica.

        Queda la tasa a la vista con su botón de refrescar, que sí informa: dice
        con qué número se está calculando.
      */}
    </Box>
  );
};


const KPICards = ({
  totalUSD,
  totalVES,
  totalFacturas,
  totalClientes,
  cambioIngresos,
  cambioFacturas,
  cambioClientes,
  exchangeRate,
  onRateChange
}) => {

  // Añadir log para ver las props recibidas
  console.log('Props received by KPICards:', { totalUSD, totalVES, totalFacturas, totalClientes, cambioIngresos, cambioFacturas, cambioClientes, exchangeRate });

  // Función para formatear números (MÁS ROBUSTA)
  const formatNumber = (value) => {
    // Verificar si es un número válido
    if (typeof value !== 'number' || isNaN(value)) {
      return '0'; // Devolver '0' si no es un número válido
    }
    // Formatear solo si es un número válido
    return new Intl.NumberFormat('es-ES').format(Math.round(value));
  };

  // Función para mostrar la tendencia (MÁS ROBUSTA)
  const renderTrend = (value) => {
    // Verificar si es un número válido
    if (typeof value !== 'number' || isNaN(value)) {
      return ( // Mostrar un guión o nada si no es válido
        <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem', color: '#8C8C8C' }}>
          - %
        </Typography>
      );
    }

    const isPositive = value >= 0;
    const displayValue = Math.abs(value).toFixed(1); // Mostrar 1 decimal

    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        color: isPositive ? '#4CAF50' : '#F44336' // Verde si positivo/cero, Rojo si negativo
      }}>
        {isPositive ?
          <TrendingUpIcon sx={{ fontSize: '0.9rem', mr: 0.2 }} /> :
          <TrendingDownIcon sx={{ fontSize: '0.9rem', mr: 0.2 }} />
        }
        <Typography
          variant="caption"
          sx={{ fontWeight: 500, fontSize: '0.7rem' }}
        >
          {isPositive ? '+' : ''}{displayValue}%
        </Typography>
      </Box>
    );
  };

  // Función auxiliar para asegurar que la tasa de cambio es válida
  const getValidExchangeRate = (rate) => {
      if (typeof rate === 'number' && !isNaN(rate) && rate > 0) {
          return rate;
      }
      return 1; // Devuelve 1 si la tasa no es válida para evitar división por cero
  }

  const validExchangeRate = getValidExchangeRate(exchangeRate);

  /*
   * Los cuatro indicadores iban cada uno de un color —verde, azul, naranja,
   * violeta— sin que el color significara nada: «Ingresos USD» no es verde por
   * ningún motivo y «Clientes activos» no es violeta. Cuatro colores decorativos
   * en la primera fila de la pantalla es lo que hace que un panel se vea como
   * cualquier otro panel.
   *
   * Ahora el número va en el color del texto y el ícono en gris. El color queda
   * disponible para lo único que lo necesita: la variación, que sí significa
   * algo —verde si subió, rojo si bajó— y ahora se lee de inmediato porque es lo
   * único con color en toda la fila.
   */
  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {/* INGRESOS USD */}
      <Grid item xs={12} md={6} lg={3}>
        <Card sx={{
          borderRadius: '12px',
          bgcolor: '#131316',
          height: '90px',
          border: '1px solid #26262C',
          boxShadow: '0 3px 6px rgba(0, 0, 0, 0.2)',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.25)'
          }
        }}>
          <CardContent sx={{
            p: 1.5,
            height: '100%',
            position: 'relative'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography
                variant="subtitle2"
                color="#8C8C8C"
                sx={{ fontWeight: 600, fontSize: '0.75rem' }}
              >
                INGRESOS USD
              </Typography>
              <Avatar
                sx={{
                  bgcolor: 'rgba(255,255,255,0.06)',
                  color: 'rgba(255,255,255,0.55)',
                  width: 22,
                  height: 22,
                }}
              >
                <AttachMoneyIcon sx={{ fontSize: 14 }} />
              </Avatar>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#F8FAFC',
                  fontSize: '1.6rem',
                  lineHeight: 1
                }}
              >
                {/* Usar la función robusta */}
                {formatNumber(totalUSD)}
              </Typography>
              {/* Usar la función robusta */}
              {renderTrend(cambioIngresos)}
            </Box>

            <Typography
              variant="caption"
              color="#8C8C8C"
              sx={{
                fontSize: '0.65rem',
                position: 'absolute',
                bottom: 8,
                left: 12
              }}
            >
              {/* Usar tasa validada y formateador robusto */}
              ≈ Bs. {formatNumber(typeof totalUSD === 'number' ? totalUSD * validExchangeRate : 0)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* INGRESOS EN BOLÍVARES */}
      <Grid item xs={12} md={6} lg={3}>
        <Card sx={{
          borderRadius: '12px',
          bgcolor: '#131316',
          height: '90px',
          border: '1px solid #26262C',
          boxShadow: '0 3px 6px rgba(0, 0, 0, 0.2)',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.25)'
          }
        }}>
          <CardContent sx={{
            p: 1.5,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography
                variant="subtitle2"
                color="#8C8C8C"
                sx={{ fontWeight: 600, fontSize: '0.75rem' }}
              >
                INGRESOS Bs.
              </Typography>
              <Avatar
                sx={{
                  bgcolor: 'rgba(255,255,255,0.06)',
                  color: 'rgba(255,255,255,0.55)',
                  width: 22,
                  height: 22,
                }}
              >
                <AttachMoneyIcon sx={{ fontSize: 14 }} />
              </Avatar>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#F8FAFC',
                  fontSize: '1.6rem',
                  lineHeight: 1
                }}
              >
                {/* Usar la función robusta */}
                {formatNumber(totalVES)}
              </Typography>
              {/* Usar la función robusta */}
              {renderTrend(cambioIngresos)}
            </Box>

            <Box sx={{ mt: 'auto', position: 'absolute', bottom: 8, right: 12 }}>
              <CompactExchangeRateSelector
                onRateChange={onRateChange}
                initialRate={exchangeRate}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* FACTURACIONES */}
      <Grid item xs={12} md={6} lg={3}>
        <Card sx={{
          borderRadius: '12px',
          bgcolor: '#131316',
          height: '90px',
          border: '1px solid #26262C',
          boxShadow: '0 3px 6px rgba(0, 0, 0, 0.2)',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.25)'
          }
        }}>
          <CardContent sx={{
            p: 1.5,
            height: '100%',
            position: 'relative'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography
                variant="subtitle2"
                color="#8C8C8C"
                sx={{ fontWeight: 600, fontSize: '0.75rem' }}
              >
                FACTURACIONES
              </Typography>
              <Avatar
                sx={{
                  bgcolor: 'rgba(255,255,255,0.06)',
                  color: 'rgba(255,255,255,0.55)',
                  width: 22,
                  height: 22,
                }}
              >
                <ReceiptIcon sx={{ fontSize: 14 }} />
              </Avatar>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#F8FAFC',
                  fontSize: '1.6rem',
                  lineHeight: 1
                }}
              >
                {/* Mostrar directamente o '0' si no es número */}
                {typeof totalFacturas === 'number' && !isNaN(totalFacturas) ? totalFacturas : '0'}
              </Typography>
              {/* Usar la función robusta */}
              {renderTrend(cambioFacturas)}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* CLIENTES ACTIVOS */}
      <Grid item xs={12} md={6} lg={3}>
        <Card sx={{
          borderRadius: '12px',
          bgcolor: '#131316',
          height: '90px',
          border: '1px solid #26262C',
          boxShadow: '0 3px 6px rgba(0, 0, 0, 0.2)',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.25)'
          }
        }}>
          <CardContent sx={{
            p: 1.5,
            height: '100%',
            position: 'relative'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography
                variant="subtitle2"
                color="#8C8C8C"
                sx={{ fontWeight: 600, fontSize: '0.75rem' }}
              >
                CLIENTES ACTIVOS
              </Typography>
              <Avatar
                sx={{
                  bgcolor: 'rgba(255,255,255,0.06)',
                  color: 'rgba(255,255,255,0.55)',
                  width: 22,
                  height: 22,
                }}
              >
                <PeopleIcon sx={{ fontSize: 14 }} />
              </Avatar>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#F8FAFC',
                  fontSize: '1.6rem',
                  lineHeight: 1
                }}
              >
                 {/* Mostrar directamente o '0' si no es número */}
                 {typeof totalClientes === 'number' && !isNaN(totalClientes) ? totalClientes : '0'}
              </Typography>
              {/* Usar la función robusta */}
              {renderTrend(cambioClientes)}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default KPICards;